/**
 * PipJourney - Main Game Component
 * 
 * A 20-level demo showcasing all 10 phases of speech development.
 * Premium quality matching existing PipGame.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import './PipJourney.css';

// Data
import { 
  PHASES, 
  LEVELS, 
  getPhaseById, 
  getLevelById, 
  getRandomPraise,
  resetPraiseTracking,
  matchesSpeech,
  getPhaseProgress 
} from './data/journeyLevels';

// Screens
import JourneyTitleScreen from './screens/JourneyTitleScreen';
import JourneyMap from './screens/JourneyMap';
import PhaseExplainer from './screens/PhaseExplainer';
import JourneyGameScreen from './screens/JourneyGameScreen';
import JourneyCompleteScreen from './screens/JourneyCompleteScreen';

// Hooks (from existing game)
import { useSpeechRecognition } from '../../../hooks/useSpeechRecognition';
import { useSpeechSynthesis } from '../../../hooks/useSpeechSynthesis';
import { useAudioEffects } from '../../../hooks/useAudioEffects';

// Game states
const GAME_STATES = {
  TITLE: 'title',
  MAP: 'map',
  PHASE_INTRO: 'phaseIntro',
  PLAYING: 'playing',
  LEVEL_COMPLETE: 'levelComplete',
  PHASE_COMPLETE: 'phaseComplete',
  JOURNEY_COMPLETE: 'journeyComplete'
};

export default function PipJourney({ onExit }) {
  // ==========================================
  // STATE
  // ==========================================
  const [gameState, setGameState] = useState(GAME_STATES.TITLE);
  const [currentLevelId, setCurrentLevelId] = useState(1);
  const [completedLevels, setCompletedLevels] = useState([]);
  const [currentPhaseId, setCurrentPhaseId] = useState(1);
  const [sessionStats, setSessionStats] = useState({
    startTime: null,
    wordsSpoken: 0,
    levelsCompleted: 0,
    perfectLevels: 0
  });
  const [lastPraise, setLastPraise] = useState('');
  const [devMode, setDevMode] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Refs
  const celebrationTimeoutRef = useRef(null);
  const lastMatchTimeRef = useRef(0);
  const gameScreenRef = useRef(null);

  // ==========================================
  // HOOKS
  // ==========================================
  const { 
    isListening,
    isSupported,
    transcript,
    interimTranscript,
    startListening,
    stopListening,
    clearTranscript,
    onResult,
  } = useSpeechRecognition();

  const { 
    speak, 
    speakAsPip,
    speakPraise,
    cancel: cancelSpeech, 
    isSpeaking 
  } = useSpeechSynthesis();
  
  const { 
    playSuccess, 
    playClick, 
    playWhoosh, 
    playPop, 
    playFanfare 
  } = useAudioEffects(true);

  // ==========================================
  // DERIVED STATE
  // ==========================================
  const currentLevel = getLevelById(currentLevelId);
  const currentPhase = getPhaseById(currentPhaseId);
  const progress = getPhaseProgress(currentLevelId);

  // ==========================================
  // EFFECTS
  // ==========================================
  
  // Reset praise tracking on mount
  useEffect(() => {
    resetPraiseTracking();
  }, []);

  // Handle speech recognition results
  useEffect(() => {
    onResult((text) => {
      if (gameState === GAME_STATES.PLAYING && !isProcessing) {
        checkWord(text);
      }
    });
  }, [gameState, isProcessing, currentLevel]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (celebrationTimeoutRef.current) {
        clearTimeout(celebrationTimeoutRef.current);
      }
      cancelSpeech?.();
      stopListening?.();
    };
  }, [cancelSpeech, stopListening]);

  // Dev mode keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl/Cmd + Shift + D to toggle dev mode
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'D') {
        e.preventDefault();
        setDevMode(prev => !prev);
        playClick?.();
      }
      // Arrow keys for navigation in dev mode
      if (devMode) {
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          skipLevel();
        }
        if (e.key === 'ArrowLeft' && currentLevelId > 1) {
          e.preventDefault();
          goToPreviousLevel();
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [devMode, currentLevelId, playClick]);

  // ==========================================
  // GAME LOGIC
  // ==========================================
  
  const checkWord = useCallback((text) => {
    if (!currentLevel || isProcessing) return;
    
    // Debounce - prevent rapid triggers
    const now = Date.now();
    if (now - lastMatchTimeRef.current < 1500) return;
    
    const result = matchesSpeech(text, currentLevel);
    
    if (result.matched) {
      lastMatchTimeRef.current = now;
      setIsProcessing(true);
      handleLevelSuccess(result);
    }
  }, [currentLevel, isProcessing]);

  const handleLevelSuccess = useCallback((result) => {
    stopListening?.();
    clearTranscript?.();
    
    // Update stats
    setSessionStats(prev => ({
      ...prev,
      wordsSpoken: prev.wordsSpoken + (currentLevel?.target?.split(' ').length || 1),
      levelsCompleted: prev.levelsCompleted + 1,
      perfectLevels: result.exact ? prev.perfectLevels + 1 : prev.perfectLevels
    }));
    
    // Mark level complete
    setCompletedLevels(prev => [...new Set([...prev, currentLevelId])]);
    
    // Get and speak praise
    const praise = getRandomPraise();
    setLastPraise(praise);
    playSuccess?.();
    speakPraise?.(praise);
    
    // Show level complete state
    setGameState(GAME_STATES.LEVEL_COMPLETE);
    
    // Trigger scene celebration if available
    gameScreenRef.current?.celebrate?.();
    
    // Auto-advance after celebration
    celebrationTimeoutRef.current = setTimeout(() => {
      advanceToNextLevel();
    }, 2500);
  }, [currentLevelId, currentLevel, stopListening, clearTranscript, playSuccess, speakPraise]);

  const advanceToNextLevel = useCallback(() => {
    const nextLevelId = currentLevelId + 1;
    const nextLevel = getLevelById(nextLevelId);
    
    setIsProcessing(false);
    
    if (!nextLevel) {
      // Journey complete!
      setGameState(GAME_STATES.JOURNEY_COMPLETE);
      playFanfare?.();
      return;
    }
    
    // Check if entering new phase
    if (nextLevel.phase !== currentPhaseId) {
      // Phase complete celebration
      setGameState(GAME_STATES.PHASE_COMPLETE);
      playFanfare?.();
      
      celebrationTimeoutRef.current = setTimeout(() => {
        setCurrentLevelId(nextLevelId);
        setCurrentPhaseId(nextLevel.phase);
        setGameState(GAME_STATES.PHASE_INTRO);
      }, 3000);
    } else {
      // Same phase, continue to next level
      setCurrentLevelId(nextLevelId);
      startLevel(nextLevelId);
    }
  }, [currentLevelId, currentPhaseId, playFanfare]);

  // ==========================================
  // NAVIGATION HANDLERS
  // ==========================================

  const startGame = useCallback(() => {
    setSessionStats({
      startTime: Date.now(),
      wordsSpoken: 0,
      levelsCompleted: 0,
      perfectLevels: 0
    });
    setCompletedLevels([]);
    setCurrentLevelId(1);
    setCurrentPhaseId(1);
    setIsProcessing(false);
    resetPraiseTracking();
    
    playWhoosh?.();
    setGameState(GAME_STATES.PHASE_INTRO);
  }, [playWhoosh]);

  const openMap = useCallback(() => {
    stopListening?.();
    playClick?.();
    setGameState(GAME_STATES.MAP);
  }, [stopListening, playClick]);

  const selectLevel = useCallback((levelId) => {
    const level = getLevelById(levelId);
    if (!level) return;
    
    playClick?.();
    setCurrentLevelId(levelId);
    setCurrentPhaseId(level.phase);
    
    // Check if we need to show phase intro
    const needsPhaseIntro = level.phase !== currentPhaseId && 
      !completedLevels.some(l => getLevelById(l)?.phase === level.phase);
    
    if (needsPhaseIntro) {
      setGameState(GAME_STATES.PHASE_INTRO);
    } else {
      startLevel(levelId);
    }
  }, [currentPhaseId, completedLevels, playClick]);

  const startLevel = useCallback((levelId) => {
    const lid = levelId || currentLevelId;
    const level = getLevelById(lid);
    if (!level) return;
    
    setGameState(GAME_STATES.PLAYING);
    setIsProcessing(false);
    clearTranscript?.();
    
    // Start listening after a short delay
    setTimeout(() => {
      startListening?.();
    }, 500);
    
    // Speak instruction if audio prompt enabled
    if (level.audioPrompt && speak) {
      setTimeout(() => {
        speak(`Say ${level.target?.toUpperCase() || 'the word'}!`);
      }, 800);
    }
  }, [currentLevelId, startListening, clearTranscript, speak]);

  const handlePhaseIntroComplete = useCallback(() => {
    startLevel(currentLevelId);
  }, [currentLevelId, startLevel]);

  const skipLevel = useCallback(() => {
    if (!devMode) return;
    
    stopListening?.();
    setCompletedLevels(prev => [...new Set([...prev, currentLevelId])]);
    advanceToNextLevel();
  }, [devMode, currentLevelId, stopListening, advanceToNextLevel]);

  const goToPreviousLevel = useCallback(() => {
    if (!devMode || currentLevelId <= 1) return;
    
    const prevLevelId = currentLevelId - 1;
    const prevLevel = getLevelById(prevLevelId);
    
    setCurrentLevelId(prevLevelId);
    setCurrentPhaseId(prevLevel?.phase || 1);
    startLevel(prevLevelId);
  }, [devMode, currentLevelId, startLevel]);

  const restartJourney = useCallback(() => {
    if (celebrationTimeoutRef.current) {
      clearTimeout(celebrationTimeoutRef.current);
    }
    cancelSpeech?.();
    stopListening?.();
    playClick?.();
    startGame();
  }, [cancelSpeech, stopListening, playClick, startGame]);

  const handleExit = useCallback(() => {
    if (celebrationTimeoutRef.current) {
      clearTimeout(celebrationTimeoutRef.current);
    }
    cancelSpeech?.();
    stopListening?.();
    onExit?.();
  }, [cancelSpeech, stopListening, onExit]);

  // ==========================================
  // RENDER
  // ==========================================
  
  // Browser not supported
  if (!isSupported) {
    return (
      <div className="pip-journey pip-journey--error">
        <div className="pip-journey__error-card">
          <span className="pip-journey__error-icon">🎤</span>
          <h2>Microphone Required</h2>
          <p>Pip's Journey needs speech recognition to work. Please use Chrome, Edge, or Safari.</p>
          <button 
            onClick={handleExit} 
            className="pip-journey__btn pip-journey__btn--primary"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pip-journey" data-state={gameState}>
      {/* Dev Mode Indicator */}
      {devMode && (
        <div className="pip-journey__dev-bar">
          <span>🛠 DEV MODE</span>
          <span>Level {currentLevelId}/{LEVELS.length}</span>
          <span>Phase {currentPhaseId}/{PHASES.length}</span>
          <span>← → to navigate</span>
        </div>
      )}

      {/* Title Screen */}
      {gameState === GAME_STATES.TITLE && (
        <JourneyTitleScreen 
          onStart={startGame}
          onExit={handleExit}
        />
      )}

      {/* Journey Map */}
      {gameState === GAME_STATES.MAP && (
        <JourneyMap
          phases={PHASES}
          levels={LEVELS}
          currentLevelId={currentLevelId}
          completedLevels={completedLevels}
          onSelectLevel={selectLevel}
          onClose={() => startLevel()}
          devMode={devMode}
        />
      )}

      {/* Phase Intro */}
      {gameState === GAME_STATES.PHASE_INTRO && (
        <PhaseExplainer
          phase={currentPhase}
          phaseNumber={currentPhaseId}
          totalPhases={PHASES.length}
          onContinue={handlePhaseIntroComplete}
          onOpenMap={openMap}
        />
      )}

      {/* Main Gameplay */}
      {(gameState === GAME_STATES.PLAYING || gameState === GAME_STATES.LEVEL_COMPLETE) && (
        <JourneyGameScreen
          ref={gameScreenRef}
          level={currentLevel}
          phase={currentPhase}
          progress={progress}
          isListening={isListening}
          transcript={interimTranscript || transcript}
          isComplete={gameState === GAME_STATES.LEVEL_COMPLETE}
          lastPraise={lastPraise}
          onOpenMap={openMap}
          onSkip={devMode ? skipLevel : undefined}
          onToggleMic={() => isListening ? stopListening?.() : startListening?.()}
          isSpeaking={isSpeaking}
        />
      )}

      {/* Phase Complete Celebration */}
      {gameState === GAME_STATES.PHASE_COMPLETE && (
        <div className="pip-journey__phase-complete">
          <div className="pip-journey__phase-complete-content">
            <div className="pip-journey__phase-badge">
              <span className="pip-journey__phase-badge-icon">{currentPhase?.icon}</span>
            </div>
            <h2 className="pip-journey__phase-complete-title">
              Phase {currentPhaseId} Complete!
            </h2>
            <p className="pip-journey__phase-complete-name">
              {currentPhase?.name}
            </p>
            <div className="pip-journey__phase-complete-stars">
              ⭐ ⭐ ⭐
            </div>
          </div>
        </div>
      )}

      {/* Journey Complete */}
      {gameState === GAME_STATES.JOURNEY_COMPLETE && (
        <JourneyCompleteScreen
          stats={sessionStats}
          completedLevels={completedLevels}
          totalLevels={LEVELS.length}
          onRestart={restartJourney}
          onExit={handleExit}
        />
      )}
    </div>
  );
}
